<template>
  <div v-if="product">
    <div class="product-page">
      <div class="product-page-top">
        <div class="product-page-images">
          <div class="product-page-images-previews">
            <div
              class="preview"
              v-for="(image, i) in images"
              :key="i"
              :style="{
                backgroundImage: `url(${getProductThumbnail(
                  image,
                  product.store
                )}), url(${image})`
              }"
              @click="setSelectedImage(image)"
              @mouseover="setSelectedImage(image)"
            ></div>
          </div>
          <div
            class="product-page-images-main"
            :style="{ backgroundImage: `url(${this.selectedImage})` }"
          ></div>
        </div>
        <div class="product-page-images-previews--mobile">
          <b-carousel v-model="slide" :interval="0" controls indicators>
            <b-carousel-slide
              v-for="(image, j) in images"
              img-width="200"
              img-height="200"
              :key="j"
              :img-src="image"
            />
          </b-carousel>
        </div>
        <div class="product-page-information">
          <div class="title-icon-row">
            <h3>{{ product.name }}</h3>
          </div>
          <p class="info">
            <b>{{ "product_page.titles.brand" | translate }}:</b>
            {{ product.brand }}
          </p>
          <p class="info">
            <b>{{ "product_page.titles.store" | translate }}:</b>
            {{ `store.${product.store}` | translate }}
          </p>
          <div class="prices">
            <p class="price" :class="{ crossed: product.priceSale }">
              {{ product.priceOriginal | currency }}
            </p>
            <p v-if="product.priceSale" class="price sale">
              {{ product.priceSale | currency }}
            </p>
            <p
              v-if="product.sustainable"
              class="price sustainable"
              v-b-tooltip.hover
              :title="`product_page.tooltips.sustainable` | translate"
            ></p>
          </div>
          <v-select
            :options="product.sizes"
            :placeholder="'product_page.titles.choose_size' | translate"
            class="sizes"
          ></v-select>
          <div class="map-button">
            <p @click="onMapIconClicked">
              {{ "product_page.titles.locate" | translate }}
            </p>
          </div>
          <div class="buttons">
            <a
              class="buy-button"
              :href="product.productUrl"
              target="_blank"
              @click="onBuyClicked"
            >
              {{ "product_page.titles.purchase_now" | translate }}
            </a>
            <div class="wishlist-button hidden">
              <wishlist-component :product="product" />
            </div>
          </div>
        </div>
      </div>
      <div class="product-page-more">
        <div
          v-for="(similar, i) in similars"
          :key="i"
          class="recommendations-content"
        >
          <h3>{{ similar.title | translate }}</h3>
          <div class="product-row">
            <product-card
              v-for="(product, j) in similar.items"
              :key="j"
              :product="product"
              @card-clicked="visitProductPage"
              class="product-card--general"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="empty-page">
    <gyra-spinner />
  </div>
</template>

<script>
import { getProductInfo } from "../services/api";
import { getSimilars } from "../services/api";
import ProductCard from "../components/molecules/product-card";
import WishlistComponent from "../components/atoms/wishlist-component";
import GyraSpinner from "../components/atoms/gyra-spinner";
import { errorHandling } from "../mixins";
import { productImageResizer } from "../mixins";
import { mapMutations } from "vuex";

export default {
  name: "ProductPage",
  components: { GyraSpinner, WishlistComponent, ProductCard },
  mixins: [errorHandling, productImageResizer],
  data() {
    return {
      product: null,
      selectedImage: null,
      similars: [],
      slide: 0
    };
  },
  methods: {
    async getProduct() {
      const response = await getProductInfo(this.$route.params.id);
      if (response.status === 200) {
        this.product = response.data;
        this.selectedImage = this.product.imageSources[0];
      } else {
        this.handleHttpError(response);
      }
    },
    onMapIconClicked() {
      this.setProducts([this.product]);
      this.$router.push({ name: "ProductMap" });
    },
    onBuyClicked() {
      if (this.$cookies.get("accept-cookie-policy")) {
        this.$gtag.event("productUrlClicked", {
          event_category: "referals",
          event_label: "product",
          value: this.product._id
        });
      }
    },
    async getSimilars() {
      const response = await getSimilars(this.$route.params.id, 4);
      if (response.status === 200) {
        this.similars = response.data;
      } else {
        this.handleHttpError(response);
      }
    },
    visitProductPage(product) {
      this.$router.push({
        name: "ProductPage",
        params: {
          name: product.name,
          id: product._id,
          sex: this.$route.params.sex
        }
      });
    },
    setSelectedImage(image) {
      this.selectedImage = image;
    },
    ...mapMutations({
      setProducts: "overview/setProducts"
    })
  },
  computed: {
    images() {
      return this.product ? this.product.imageSources.slice(0, 10) : null;
    }
  },
  beforeMount() {
    this.getProduct();
    this.getSimilars();
  },
  watch: {
    $route() {
      this.getProduct();
    }
  }
};
</script>

<style scoped lang="scss">
@import "~@/styles/global.scss";
$margin-lg: 50px;
$margin-sm: 100px;
.product-page {
  display: flex;
  flex-direction: column;
}
.product-page-top {
  margin-top: 0;
  width: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (min-width: 550px) {
    margin: 40px $margin-lg 40px;
  }
  @media (min-width: 1165px) {
    flex-direction: row;
    margin: 40px $margin-lg 40px;
  }
}
.product-page-information {
  flex-shrink: 0;
  width: auto;
  margin: 0 20px 50px 20px;
  @media (min-width: 1165px) {
    flex-shrink: 1;
    max-width: 700px;
    margin: 0 $margin-lg;
    width: 100%;
  }

  h3 {
    width: 90%;
  }
}
.title-icon-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.map-button {
  margin-top: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  width: 100%;
  p {
    background-color: $gyra-mint;
    border-radius: 12px;
    height: 40px;
    width: 100%;
    margin: 0;
    cursor: pointer;
    font-size: 14px;
    font-weight: bold;
    text-align: center;
  }
}
.hidden {
  display: none !important;
}
.product-page-images {
  width: auto;
  display: none;
  flex-direction: row;
  justify-content: center;
  margin-bottom: 40px;
  @media (min-width: 550px) {
    display: flex;
  }
  @media (min-width: 1165px) {
    margin-bottom: 40px;
  }
}
.product-page-images-previews {
  height: 500px;
  flex-direction: column;
  justify-content: start;
  overflow: scroll;
  @media (min-width: 1165px) {
    height: 800px;
  }
  -ms-overflow-style: none;
}
.product-page-images-previews::-webkit-scrollbar {
  display: none;
}

.product-page-images-previews--mobile {
  flex-shrink: 0;
  width: 100%;
  height: 500px;
  display: flex;
  margin-bottom: 40px;
  @media (min-width: 550px) {
    display: none;
  }
  overflow: hidden;
}
.preview {
  flex-shrink: 0;
  height: 180px;
  width: 150px;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  &:not(:last-child) {
    margin-bottom: 20px;
  }
}
.product-page-images-main {
  margin-left: 50px;
  width: 400px;
  height: 500px;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  @media (min-width: 1165px) {
    width: 500px;
    height: 800px;
  }
}
h3 {
  font-weight: bold;
  line-height: 32px;
  font-size: 32px;
  @media (min-width: 1000px) {
    line-height: 32px;
    font-size: 32px;
  }
  @media (min-width: 1200px) {
    line-height: 46px;
    font-size: 46px;
  }
}
.info {
  @media (min-width: $gyra-breakpoint-md) {
    margin-top: 40px;
  }
  margin-top: 16px;
  margin-bottom: 0;
  font-size: 20px;
}
.crossed {
  text-decoration: line-through;
}
.prices {
  display: flex;
  flex-direction: row;
}
.price {
  @media (min-width: $gyra-breakpoint-md) {
    margin-top: 40px;
  }
  margin-bottom: 0;
  margin-top: 16px;
  font-size: 20px;
}
.sale {
  margin-left: 50px;
  color: $gyra-red;
  font-weight: 500;
}
.sustainable {
  margin-left: auto;
  &::after {
    content: "🌱";
  }
}
.product-row {
  margin: 20px 20px 40px 20px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  @media (min-width: 910px) {
    flex-wrap: nowrap;
    margin: 20px $margin-lg 40px $margin-lg;
  }
}
.product-page-more {
  margin-top: 0;
  @media (min-width: $gyra-breakpoint-md) {
    margin-top: 50px;
  }
  h3 {
    margin: 20px 20px 40px 20px;
    @media (min-width: 910px) {
      margin: 20px $margin-lg 40px $margin-lg;
    }
  }
}
.sizes {
  margin-top: 25px;
  border-radius: 6px;
}
.buttons {
  display: flex;
  flex-direction: row;
}
.buy-button {
  // uncomment with re-addition of wishlist
  /*margin-right: 10px;*/
  /*width: 90%;*/
  width: 100%;
  background-color: $gyra-salmon;
  border-radius: 10px;
  height: 40px;
  font-weight: bold;
  font-size: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: inherit;
  text-decoration: none;
}
.wishlist-button {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid $gyra-salmon;
  margin-left: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 40px;
  cursor: pointer;
}
.notice {
  margin-top: 25px;
}
.empty-page {
  height: 100vh;
}
</style>
